import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatDividerModule } from '@angular/material/divider'
import { AsyncPipe, CommonModule } from '@angular/common'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { Call, CallsService, CallTimelineResponse, CallTimelineUserActivity } from '../calls.service'
import { AvatarComponent } from '../avatar/avatar.component'

@Component({
  selector: 'app-call-timeline-dialog',
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    MatDialogModule,
    MatIconModule,
    MatDividerModule,
    TranslatePipe,
    TranslateDirective,
    MatTooltipModule,
    MatProgressSpinner,
    AvatarComponent,
    MatButton,
    MatIconButton,
  ],
  templateUrl: './call-timeline-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './call-timeline-dialog.component.scss',
})
export class CallTimelineDialogComponent {
  call: Call = inject(MAT_DIALOG_DATA)
  callsService = inject(CallsService)
  sortAscending = false

  timeline$ = this.callsService.getCallTimeline(this.call.id)

  toggleSort(): void {
    this.sortAscending = !this.sortAscending
  }

  getSortedEvents(events: any[]): any[] {
    if (!events) return []
    return this.sortAscending
      ? [...events].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      : [...events].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
  }

  getEventIcon(type: string): string {
    switch (type) {
      case 'CREATE_MEETING':
        return 'add_circle'
      case 'TOGGLE_ON_MEETING':
        return 'toggle_on'
      case 'TOGGLE_OFF_MEETING':
        return 'toggle_off'
      case 'POSTPONE_MEETING':
        return 'schedule'
      case 'UPLOAD_RESOURCE':
        return 'upload_file'
      case 'DELETE_RESOURCE':
        return 'delete_outline'
      case 'JOIN_MEETING_NOW':
        return 'play_arrow'
      case 'JOIN_MEETING':
        return 'meeting_room'
      case 'PAUSE_RECORDING':
        return 'pause_circle'
      case 'RESUME_RECORDING':
        return 'play_circle'
      case 'HANGUP_MEETING':
        return 'call_end'
      case 'LEAVE_MEETING':
        return 'exit_to_app'
      case 'COMPLETE_MINUTES_GENERATION':
        return 'check_circle'
      case 'VIEW_CALL':
        return 'visibility'
      case 'DOWNLOAD_VIDEO_RECORDING':
        return 'videocam'
      case 'DOWNLOAD_AUDIO_RECORDING':
        return 'audiotrack'
      case 'DOWNLOAD_TRANSCRIPT':
        return 'description'
      case 'DOWNLOAD_MINUTES':
        return 'summarize'
      case 'DOWNLOAD_OUTLINE':
        return 'list_alt'
      case 'DOWNLOAD_ACTION_ITEMS':
        return 'list_alt'
      case 'REPROCESS_OUTPUTS':
        return 'refresh'
      case 'SHARE_MEETING_WITH_EXISTING_USER':
      case 'SHARE_MEETING_WITH_NEW_USER':
        return 'person_add'
      case 'ADJUST_RETENTION_PERIOD':
        return 'event_repeat'
      default:
        return 'info'
    }
  }

  getEventColor(type: string): string {
    switch (type) {
      case 'CREATE_MEETING':
        return 'primary'
      case 'TOGGLE_ON_MEETING':
        return 'success'
      case 'TOGGLE_OFF_MEETING':
        return 'error'
      case 'POSTPONE_MEETING':
        return 'warning'
      case 'UPLOAD_RESOURCE':
        return 'info'
      case 'DELETE_RESOURCE':
        return 'warning'
      case 'JOIN_MEETING_NOW':
        return 'accent'
      case 'JOIN_MEETING':
        return 'success'
      case 'PAUSE_RECORDING':
        return 'warning'
      case 'RESUME_RECORDING':
        return 'success'
      case 'HANGUP_MEETING':
        return 'error'
      case 'LEAVE_MEETING':
        return 'default'
      case 'COMPLETE_MINUTES_GENERATION':
        return 'success'
      case 'VIEW_CALL':
        return 'info'
      case 'DOWNLOAD_VIDEO_RECORDING':
      case 'DOWNLOAD_AUDIO_RECORDING':
      case 'DOWNLOAD_TRANSCRIPT':
      case 'DOWNLOAD_MINUTES':
      case 'DOWNLOAD_OUTLINE':
      case 'DOWNLOAD_ACTION_ITEMS':
        return 'info'
      case 'REPROCESS_OUTPUTS':
        return 'accent'
      case 'SHARE_MEETING_WITH_EXISTING_USER':
      case 'SHARE_MEETING_WITH_NEW_USER':
        return 'primary'
      case 'ADJUST_RETENTION_PERIOD':
        return 'warning'
      default:
        return 'default'
    }
  }

  getUsersWhoEnabledAssistant(data: CallTimelineResponse): CallTimelineUserActivity[] {
    return data.userActivities.filter((user) => user.hasEnabledAssistant)
  }

  getUsersWhoViewed(data: CallTimelineResponse): CallTimelineUserActivity[] {
    return data.userActivities.filter((user) => user.viewCount > 0)
  }
}
